import { Inject, Injectable, Injector } from '@angular/core';
import type { AccountInfo } from '@azure/msal-common';
import { firstValueFrom, from, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { GlobalVariables, ResourceType } from 'app/services/globals';
import { IAzureResource } from 'app/interfaces/IAzureResource';
import {
    IDebugSessionResponseWrapper,
    IDeleteDebugSessionRequest,
    IExecuteDataFlowDebugQueryResponseWrapper,
    IExecuteDataFlowDebugRunRequest,
    IExecuteDataFlowDebugRunResponse,
    IExecuteDebugCommandPayload,
    IQueryAllDebugSessionsResponse,
    IStartDebugSessionRequest
} from 'app/interfaces/IDataFlowDebug';
import {
    CommonIFrameMessageType,
    ICreateArtifactResponseData,
    IFrameDebugAction,
    IFrameLogicAppsActivityAction,
    IIFrameActivityRunData,
    IIFrameAddActivityData,
    IIFrameAsyncOperationResult,
    IIframeCallApiServiceRequestData,
    IIFrameCancelCopyJobRunRequestData,
    IIFrameCancelCopyJobRunResponseData,
    IIFrameCancelPipelineRunRequestData,
    IIFrameCancelPipelineRunResponseData,
    IIFrameCloseSideNavRequestData,
    IIFrameConnectorRequestData,
    IIFrameCopilotAvailabilityRequestData,
    IIFrameCopilotAvailabilityResponseData,
    IIFrameCosmosDbWorkloadRequestData,
    IIFrameCosmosDbWorkloadResponseData,
    IIFrameCreateArtifactRequestData,
    IIFrameCreateCopyJobRunRequestData,
    IIFrameCreateCopyJobRunResponseData,
    IIFrameCreatePipelineRequestData,
    IIFrameCreatePipelineRunRequestData,
    IIFrameCreatePipelineRunResponseData,
    IIFrameCreateTumblingWindowTriggerRequestData,
    IIFrameCreateTumblingWindowTriggerResponseData,
    IIFrameDeleteTumblingWindowTriggerRequestData,
    IIFrameDebugActionData,
    IIFrameDebugRequestData,
    IIFrameDeleteResourceRequestData,
    IIFrameDiscardChangeData,
    IIFrameDynamicInvokeRequestData,
    IIFrameEditTriggerRequestData,
    IIFrameExecuteCopilotAppActionData,
    IIFrameFetchJobHistoryRequestData,
    IIFrameFetchJobInstanceRequestData,
    IIFrameGetActivityRunProgressRequestData,
    IIFrameGetActivityRunProgressResponseData,
    IIFrameGetConnectionAuthoringLinkRequestData,
    IIFrameGetAllAccountsResultData,
    IIFrameGetCopyJobActivityRunProgressResponseData,
    IIFrameGetDataflowParametersResponseData,
    IIFrameGetDataRequestData,
    IIFrameGetTumblingWindowTriggerRequestData,
    IIFrameGetTumblingWindowTriggerResponseData,
    IIFrameGetECFeaturesListRequestData,
    IIFrameGetECFeaturesListResponseData,
    IIFrameGetECValueRequestData,
    IIFrameGetECValueResponseData,
    IIFrameGetExternalPortalLinkResponseData,
    IIFrameGetLibraryVariablesResponseData,
    IIFrameGetOneLakeApiEndpointResponseData,
    IIFrameGetPipelineCopilotContextResponseData,
    IIFrameGetResourcesRequestData,
    IIFrameGetResourcesResponseData,
    IIFrameGetRibbonButtonLocationResponse,
    IIFrameGetRibbonButtonRectRequest,
    IIFrameGetTokenResultData,
    IIFrameGetTriggersRequestData,
    IIFrameGetTriggersResponseData,
    IIFrameGetUserProfileResultData,
    IIFrameGetWorkspaceEndpointsRequestData,
    IIFrameGetWorkspaceEndpointsResponseData,
    IIFrameGrantPermissionRequestData,
    IIFrameHttpResponseData,
    IIFrameImportPipelineTemplateData,
    IIFrameListResourcesMessageData,
    IIFrameLoadMonitoringData,
    IIFrameLoginPopupResultData,
    IIFrameLogicAppsAuthenticationResponseData,
    IIFrameMessage,
    IIFrameNavigateRequestData,
    IIFrameOpenArtifactRequestData,
    IIFrameOpenCopyAssistantData,
    IIFrameOpenCopyJobRunPageRequestData,
    IIFrameOpenCopyJobWizardData,
    IIFrameOpenDbtJobWizardData,
    IIFrameOpenDialogRequestData,
    IIFrameOpenGetDataWizardData,
    IIFrameOpenLibraryVariablePickerResponseData,
    IIFrameOpenMonitoringPageRequestData,
    IIFrameOpenMountedDataFactory,
    IIFrameOpenMountWizardData,
    IIFrameOpenCreateTumblingWindowTriggerRequestData,
    IIFrameOpenManageTumblingWindowTriggersRequestData,
    IIFrameOpenPageRequestData,
    IIFrameOpenPanelRequestData,
    IIFrameOpenResourceData,
    IIFrameOpenResourceInDedicatedIFrameData,
    IIFrameOpenResourceInTridentWsData,
    IIFrameOpenTemplateGalleryData,
    IIFramePushJobStateRequestData,
    IIFrameQueryActivityRunsRequestData,
    IIFrameQueryActivityRunsResponseData,
    IIFrameQueryCopyJobActivityRunProgressRequestData,
    IIFrameQueryCopyJobRunsRequestData,
    IIFrameQueryDbtItemRunsRequestData,
    IIFrameQueryPipelineRunRequestData,
    IIFrameQueryPipelineRunResponseData,
    IIFrameQueryPipelineRunsRequestData,
    IIFrameQueryPipelineRunsResponseData,
    IIFrameQueryTumblingWindowTriggersRequestData,
    IIFrameQueryTumblingWindowTriggersResponseData,
    ITumblingWindowTriggerListArtifactData,
    IIFrameRequestValidateData,
    IIFrameTumblingWindowTriggerOperationResponseData,
    IIFrameTumblingWindowTriggerRuntimeRequestData,
    IIFrameUpdateTumblingWindowTriggerRequestData,
    IIFrameUpdateTumblingWindowTriggerResponseData,
    IIFrameRerunPipelineRequestData,
    IIFrameRerunPipelineResponseData,
    IIFrameResetCopyJobRequestData,
    IIFrameResetCopyJobResponseData,
    IIFrameResourceActionBaseData,
    IIFrameResourceCodeData,
    IIFrameResourceContextBase,
    IIFrameResourceObjectBase,
    IIFrameResultCallback,
    IIFrameResynchronizeModelServiceResourceRequestData,
    IIFrameSaveResourceRequestData,
    IIFrameSaveResourceResponseData,
    IIFrameSaveScheduledJobsRequestData,
    IIFrameSaveScheduledJobsResponseData,
    IIFrameSaveTridentResourceData,
    IIFrameScheduleMessageData,
    IIFrameScriptData,
    IIFrameSetShowAuthoringTabData,
    IIFrameTridentWorkspaceBaseData,
    IIFrameTriggerRunData,
    IIFrameUpdateCopilotSessionData,
    IIFrameUpdateStoredArtifactsData,
    IIFrameUpdateThemeData,
    IIFrameUpdateTridentTabData,
    IIFrameZoomOptionData,
    IReflexTriggerData,
    TridentIFrameMessageType,
    IIFrameFeedbackAvailabilityResponseData,
    IIFrameMessageData,
    IIFrameOpenGetDataRequestData,
    IIFrameGetLakehouseTablesResponseData,
    IIFrameQueryCopyJobRunRequestData,
    IIFrameQueryCopyJobRunResponseData,
    IIFrameGetWorkspaceNetworkSecuritySettingsResponseData,
    IIFrameGetWorkspaceNetworkSecuritySettingsRequestData,
    IIFrameGetLibraryVariablesRequestData,
    IIFrameApproveActivityRunRequestData,
    IIFrameApproveActivityRunResponseData,
    IIFrameCreateStorageTriggerRequestData,
    IIFrameCreateStorageTriggerResponseData,
    IIFrameCreateCopyJobOneLakeEventTriggerRequestData,
    IIFrameCreateDataPipelineRequestData,
    IIFrameCreateDataPipelineResponseData,
    IIFrameUpdateDataPipelineDefinitionRequestData,
    IIFrameUpdateDataPipelineDefinitionResponseData,
    IIFrameCreateDataflowFabricRequestData,
    IIFrameCreateDataflowFabricResponseData,
    IIFrameUpdateDataflowFabricDefinitionRequestData,
    IIFrameUpdateDataflowFabricDefinitionResponseData,
    IIFrameCreateFolderRequestData,
    IIFrameCreateFolderResponseData,
    IIFrameListFoldersRequestData,
    IIFrameListFoldersResponseData,
    IIFrameGetLakehouseSchemasResponseData,
    IIFrameGetLakehouseSchemasEnabledResponseData,
    IIFrameGetNotebookContentResponseData,
    IIFrameGetEventStreamArtifactModelRequestData,
    IIFrameGetEventStreamArtifactModelResponseData,
    IIFramePreviewEventStreamRequestData,
    IIFrameOpenJSONCodeRequestData
} from 'app/interfaces/IIFrameMessage';
import type { IThemeType } from 'app/theme/interfaces';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { isTridentIframeType, queueCommand } from 'app/iframe-client/iframe-client';
import shellapi from 'a365-shell-api';
import { IRequestOptionsArgs } from './http';
import { IResponseOptionsArgs } from './http/interfaces';
import { Model } from 'app/model/backendmodel';
import { IPipelineRunResponse } from 'app/interfaces/IPipelineRunResponse';
import { IActivityRunProgress, ICopyJobActivityRunProgress } from 'app/model/monitoring/monitoring-models';
import { Metamodel } from 'app/model/base/model';
import { ModelService } from 'app/model/services/model.service';
import {
    ICloseDialogResult,
    ICloseUnifiedEditConnection,
    ICloseUnifiedGetData,
    ICopyAssistantDeploymentPayload,
    ICopyJobDeploymentPayload,
    IOpenSidenavCommandPayload,
    IOpenUnifiedEditConnection,
    IOpenUnifiedGetData,
    ITemplateDeploymentPayload
} from 'app/iframe-client/iframe-client.typings';
import { AdapterContext, type ITridentResource, LinkedService, ModelUtilties, Named, Pipeline } from 'shell-models-lib';
import { iFrameCustomerProfiles, IFrameProfile } from './iframe-customer-profiles';
import { noop, pick } from 'lodash';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';
import { BaseAppModuleManagementService } from 'app/multi-contexts/services/base-app-module-management.service';
import { I_LOGGER, ILogger } from '../logger/ILogger';
import { convertObjectToString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { ICopyJobRunResponse } from 'app/interfaces/ICopyJobRunResponse';
import type {
    ArtifactJobHistory,
    ArtifactJobInstance,
    ArtifactSchedule,
    GetReservedNamesParams
} from '@trident/data-access-artifact';
import { IScheduledJobsResponse } from 'app/interfaces/IScheduledJobsResponse';
import { tridentEcFeatures } from './exposure-control/ec.consts';
import { generateCopilotContextFromPipelineModelAsync } from 'app/shared/utils/pipeline-copilot/copilot.util';
import { IDiscoveredVariables } from 'app/interfaces/IDiscoveredVariables';
import type {
    TridentApiServiceMethodName,
    TridentApiServiceMethodResults,
    TridentApiServiceName
} from 'app/interfaces/TridentApiServiceDefinitions';
import { sleep } from 'app/shared/utils/sleep.util';
import type { OpenSubfolderPickerDialogConfig, OpenSubfolderPickerDialogResult, PromptValue } from '@trident/extension-client-common'

/**
 *  CAUTION: IFrameMessengerService is a service provided in RootModule.
 */
@Injectable({
    providedIn: 'root'
})
export class IFrameMessengerService {
    private static readonly className = 'IFrameMessengerService';

    public get message$(): Observable<IIFrameMessage> {
        return this._message$;
    }

    public readonly requestOpenResource$: Observable<
        IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenResourceData>
    >;
    public readonly requestOpenMountedDataFactory$: Observable<
        IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenMountedDataFactory>
    >;
    public readonly requestSetShowAuthoringTab$: Observable<IIFrameSetShowAuthoringTabData>;
    public readonly requestQuickRefreshTab$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestRefresh$: Observable<void>;
    public readonly requestSave$: Observable<IIFrameMessage<IIFrameSaveResourceRequestData>>;
    public readonly requestExportTemplate$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestImportTemplate$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestOpenTemplateGallery$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestDiscardAll$: Observable<IIFrameMessage<IIFrameDiscardChangeData>>;
    public readonly requestCode$: Observable<string>;
    public readonly requestInsertScript$: Observable<IIFrameScriptData>;
    public readonly requestExportScript$: Observable<string>;
    public readonly requestExportExpression$: Observable<string>;
    public readonly requestImportExpression$: Observable<IIFrameResultCallback>;
    public readonly requestLoadMonitoring$: Observable<string>;
    public readonly requestTriggerRun$: Observable<IIFrameTriggerRunData>;
    public readonly requestAddActivity$: Observable<IIFrameAddActivityData>;
    public readonly requestAddActivityJSON$: Observable<IIFrameMessage<IIFrameExecuteCopilotAppActionData>>;
    public readonly requestDetectChildPipeline$: Observable<IIFrameMessage<IIFrameResourceActionBaseData>>;
    public readonly requestDetectLogicAppsActivity$: Observable<IIFrameMessage<IIFrameResourceActionBaseData>>;
    public readonly requestOpenCreateTumblingWindowTrigger$: Observable<
        IIFrameMessage<IIFrameOpenCreateTumblingWindowTriggerRequestData>
    >;
    public readonly requestOpenManageTumblingWindowTriggers$: Observable<
        IIFrameMessage<IIFrameOpenManageTumblingWindowTriggersRequestData>
    >;
    public readonly requestGetDataModelCreation$: Observable<ICopyAssistantDeploymentPayload>;
    public readonly requestTemplateModelCreation$: Observable<ITemplateDeploymentPayload>;
    public readonly requestCopyJobCreation$: Observable<{
        payload: ICopyJobDeploymentPayload;
        resolve: () => void;
        reject: (reason?: unknown) => void;
    }>;
    public readonly requestValidate$: Observable<IIFrameMessage<IIFrameRequestValidateData>>;
    public readonly requestPublishAll$: Observable<IIFrameMessage<IIFrameResourceActionBaseData>>;
    public readonly requestSaveAll$: Observable<IIFrameMessage<IIFrameResourceActionBaseData>>;
    public readonly requestCancelRun$: Observable<IIFrameCancelPipelineRunRequestData>;
    public readonly requestResetCopyJob$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestFetchSchedule$: Observable<IIFrameResourceObjectBase>;
    public readonly requestCheckModelServiceStatus$: Observable<IIFrameMessage>;
    public readonly requestResynchronizeModelService$: Observable<IIFrameMessage<IIFrameResourceContextBase>>;
    public readonly requestUpdateStoredArtifacts$: Observable<IIFrameMessage<IIFrameUpdateStoredArtifactsData>>;
    public readonly requestCloseResource$: Observable<IIFrameMessage<IIFrameResourceActionBaseData>>;
    public readonly requestAutoAlign$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestZoomToFit$: Observable<IIFrameResourceActionBaseData>;
    public readonly requestZoom$: Observable<IIFrameZoomOptionData>;
    public readonly requestActivityRuns$: Observable<IIFrameMessage<IIFrameOpenMonitoringPageRequestData>>;
    public readonly requestCopyJobRun$: Observable<IIFrameMessage<IIFrameOpenCopyJobRunPageRequestData>>;
    public readonly requestConfirmDialogClose$: Observable<void>;
    public readonly requestRefreshPipelineRun$: Observable<IIFrameActivityRunData>;
    public readonly requestThemeUpdate$: Observable<IThemeType>;
    private readonly _requestOpenResource$: Subject<
        IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenResourceData>
    > = new Subject<IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenResourceData>>();
    private readonly _requestOpenMountedDataFactory$: Subject<
        IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenMountedDataFactory>
    > = new Subject<IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenMountedDataFactory>>();
    private readonly _requestSetShowAuthoringTab$ = new Subject<IIFrameSetShowAuthoringTabData>();
    private readonly _requestQuickRefreshTab$ = new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestRefresh$: Subject<void> = new Subject<void>();
    private readonly _requestSave$ = new Subject<IIFrameMessage<IIFrameSaveResourceRequestData>>();
    private readonly _requestCode$ = new Subject<string>();
    private readonly _requestExportTemplate$ = new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestImportTemplate$ = new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestOpenTemplateGallery$ = new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestInsertScript$ = new Subject<IIFrameScriptData>();
    private readonly _requestExportScript$ = new Subject<string>();
    private readonly _requestExportExpression$ = new Subject<string>();
    private readonly _requestImportExpression$ = new Subject<IIFrameResultCallback>();
    private readonly _requestLoadMonitoring$: Subject<string> = new Subject<string>();
    private readonly _requestTriggerRun$: Subject<IIFrameTriggerRunData> = new Subject<IIFrameTriggerRunData>();
    private readonly _requestAddActivity$: Subject<IIFrameAddActivityData> = new Subject<IIFrameAddActivityData>();
    private readonly _requestAddActivityJSON$: Subject<IIFrameMessage<IIFrameExecuteCopilotAppActionData>> =
        new Subject<IIFrameMessage<IIFrameExecuteCopilotAppActionData>>();
    private readonly _requestDetectChildPipeline$: Subject<IIFrameMessage<IIFrameResourceActionBaseData>> = new Subject<
        IIFrameMessage<IIFrameResourceActionBaseData>
    >();
    private readonly _requestDetectLogicAppsActivity$: Subject<IIFrameMessage<IIFrameResourceActionBaseData>> =
        new Subject<IIFrameMessage<IIFrameResourceActionBaseData>>();
    private readonly _requestOpenCreateTumblingWindowTrigger$ = new Subject<
        IIFrameMessage<IIFrameOpenCreateTumblingWindowTriggerRequestData>
    >();
    private readonly _requestOpenManageTumblingWindowTriggers$ = new Subject<
        IIFrameMessage<IIFrameOpenManageTumblingWindowTriggersRequestData>
    >();
    private readonly _requestValidate$: Subject<IIFrameMessage<IIFrameRequestValidateData>> = new Subject<
        IIFrameMessage<IIFrameRequestValidateData>
    >();
    private readonly _requestPublishAll$: Subject<IIFrameMessage<IIFrameResourceActionBaseData>> = new Subject<
        IIFrameMessage<IIFrameResourceActionBaseData>
    >();
    private readonly _requestSaveAll$: Subject<IIFrameMessage<IIFrameResourceActionBaseData>> = new Subject<
        IIFrameMessage<IIFrameResourceActionBaseData>
    >();
    private readonly _requestCancelRun$: Subject<IIFrameCancelPipelineRunRequestData> =
        new Subject<IIFrameCancelPipelineRunRequestData>();
    private readonly _requestResetCopyJob$: Subject<IIFrameResourceActionBaseData> =
        new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestFetchSchedule$: Subject<IIFrameResourceObjectBase> =
        new Subject<IIFrameResourceObjectBase>();
    private readonly _requestGetDataModelCreation$: Subject<ICopyAssistantDeploymentPayload> =
        new Subject<ICopyAssistantDeploymentPayload>();
    private readonly _requestTemplateModelCreation$: Subject<ITemplateDeploymentPayload> =
        new Subject<ITemplateDeploymentPayload>();
    private readonly _requestCopyJobCreation$ = new Subject<{
        payload: ICopyJobDeploymentPayload;
        resolve: () => void;
        reject: (reason?: unknown) => void;
    }>();
    private readonly _requestResynchronizeModelService$: Subject<IIFrameMessage<IIFrameResourceContextBase>> =
        new Subject<IIFrameMessage<IIFrameResourceContextBase>>();
    private readonly _requestUpdateStoredArtifacts$: Subject<IIFrameMessage<IIFrameUpdateStoredArtifactsData>> =
        new Subject<IIFrameMessage<IIFrameUpdateStoredArtifactsData>>();
    private readonly _requestCloseResource$: Subject<IIFrameMessage<IIFrameResourceActionBaseData>> = new Subject<
        IIFrameMessage<IIFrameResourceActionBaseData>
    >();
    private readonly _requestAutoAlign$: Subject<IIFrameResourceActionBaseData> =
        new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestZoomToFit$: Subject<IIFrameResourceActionBaseData> =
        new Subject<IIFrameResourceActionBaseData>();
    private readonly _requestZoom$: Subject<IIFrameZoomOptionData> = new Subject<IIFrameZoomOptionData>();
    private readonly _message$ = new Subject<IIFrameMessage>();

    private readonly _requestActivityRuns$: Subject<IIFrameMessage<IIFrameOpenMonitoringPageRequestData>> = new Subject<
        IIFrameMessage<IIFrameOpenMonitoringPageRequestData>
    >();
    private readonly _requestCopyJobRun$: Subject<IIFrameMessage<IIFrameOpenCopyJobRunPageRequestData>> = new Subject<
        IIFrameMessage<IIFrameOpenCopyJobRunPageRequestData>
    >();
    private readonly _requestDiscardAll$ = new Subject<IIFrameMessage<IIFrameDiscardChangeData>>();
    private readonly _requestConfirmDialogClose$ = new Subject<void>();
    private readonly _requestRefreshPipelineRun$: Subject<IIFrameActivityRunData> =
        new Subject<IIFrameActivityRunData>();
    private readonly _requestThemeUpdate$ = new ReplaySubject<IThemeType>(1);

    private readonly _sessionId = generateUUID();
    private readonly _initializedPromise = this.message$
        .pipe(
            filter((message) => message.type === CommonIFrameMessageType.Initialize),
            take(1)
        )
        .toPromise();

    private _parentOrigin: string | null = null;
    private _nextId = 1;
    private _saveId: string;

    // Caution! Only dependencies provided in root module should be added here, for others involving specific context info, add it in lazy-providers.ts
    constructor(
        private readonly _injector: Injector,
        @Inject(I_LOGGER) private readonly _loggerService: ILogger
    ) {
        if (!GlobalVariables.enableIFrameMode) {
            return;
        }
        if (__IS_FABRIC_DPE__ || __IS_FABRIC_ADF__) {
            this._loggerService.log(IFrameMessengerService.className, 'Created IFrameMessengerService');
        }
        this._requestConfirmDialogClose$ = new Subject<void>();
        this.requestOpenResource$ = this._requestOpenResource$;
        this.requestOpenMountedDataFactory$ = this._requestOpenMountedDataFactory$;
        this.requestSetShowAuthoringTab$ = this._requestSetShowAuthoringTab$;
        this.requestRefresh$ = this._requestRefresh$;
        this.requestSave$ = this._requestSave$;
        this.requestCode$ = this._requestCode$;
        this.requestExportTemplate$ = this._requestExportTemplate$;
        this.requestImportTemplate$ = this._requestImportTemplate$;
        this.requestOpenTemplateGallery$ = this._requestOpenTemplateGallery$;
        this.requestInsertScript$ = this._requestInsertScript$;
        this.requestExportScript$ = this._requestExportScript$;
        this.requestExportExpression$ = this._requestExportExpression$;
        this.requestImportExpression$ = this._requestImportExpression$;
        this.requestLoadMonitoring$ = this._requestLoadMonitoring$;
        this.requestTriggerRun$ = this._requestTriggerRun$;
        this.requestAddActivity$ = this._requestAddActivity$;
        this.requestAddActivityJSON$ = this._requestAddActivityJSON$;
        this.requestDetectChildPipeline$ = this._requestDetectChildPipeline$;
        this.requestDetectLogicAppsActivity$ = this._requestDetectLogicAppsActivity$;
        this.requestOpenCreateTumblingWindowTrigger$ = this._requestOpenCreateTumblingWindowTrigger$;
        this.requestOpenManageTumblingWindowTriggers$ = this._requestOpenManageTumblingWindowTriggers$;
        this.requestValidate$ = this._requestValidate$;
        this.requestPublishAll$ = this._requestPublishAll$;
        this.requestSaveAll$ = this._requestSaveAll$;
        this.requestCancelRun$ = this._requestCancelRun$;
        this.requestResetCopyJob$ = this._requestResetCopyJob$;
        this.requestFetchSchedule$ = this._requestFetchSchedule$;
        this.requestGetDataModelCreation$ = this._requestGetDataModelCreation$;
        this.requestTemplateModelCreation$ = this._requestTemplateModelCreation$;
        this.requestCopyJobCreation$ = this._requestCopyJobCreation$;
        this.requestResynchronizeModelService$ = this._requestResynchronizeModelService$;
        this.requestUpdateStoredArtifacts$ = this._requestUpdateStoredArtifacts$;
        this.requestQuickRefreshTab$ = this._requestQuickRefreshTab$;
        this.requestCloseResource$ = this._requestCloseResource$;
        this.requestAutoAlign$ = this._requestAutoAlign$;
        this.requestZoomToFit$ = this._requestZoomToFit$;
        this.requestZoom$ = this._requestZoom$;
        this.requestActivityRuns$ = this._requestActivityRuns$;
        this.requestCopyJobRun$ = this._requestCopyJobRun$;
        this.requestDiscardAll$ = this._requestDiscardAll$;
        this.requestConfirmDialogClose$ = this._requestConfirmDialogClose$;
        this.requestRefreshPipelineRun$ = this._requestRefreshPipelineRun$;
        this.requestThemeUpdate$ = this._requestThemeUpdate$;
        this._initializeIFrame();
    }

    public async grantPermission(data: IIFrameGrantPermissionRequestData): Promise<undefined> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GrantPermission,
                data
            },
            (message: IIFrameMessage<undefined>) =>
                message?.error ? Promise.reject(message.error) : Promise.resolve(message?.data)
        );
    }

    public async getReservedNames(data: GetReservedNamesParams): Promise<string[]> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetReservedNames,
                data
            },
            (message: IIFrameMessage<string[]>) =>
                message?.error ? Promise.reject(message.error) : Promise.resolve(message?.data)
        );
    }

    public async startDebugSession(debugRequest: IStartDebugSessionRequest): Promise<IDebugSessionResponseWrapper> {
        return this._debugSessionRequest({ debugRequest, debugAction: IFrameDebugAction.StartSession });
    }

    public async stopDebugSession(debugRequest: IDeleteDebugSessionRequest): Promise<void> {
        return this._debugSessionRequest({ debugRequest, debugAction: IFrameDebugAction.StopSession });
    }

    public async debugsessionGeneralPolling<T>(debugRequest: string): Promise<T> {
        return this._debugSessionRequest({ debugRequest, debugAction: IFrameDebugAction.PollSession });
    }

    public async queryAllDebugSessions(debugRequest: string): Promise<IQueryAllDebugSessionsResponse> {
        return this._debugSessionRequest({ debugRequest, debugAction: IFrameDebugAction.QuerySessions });
    }

    public async executeDebugSession(
        debugRequest: IExecuteDataFlowDebugRunRequest
    ): Promise<IExecuteDataFlowDebugRunResponse> {
        return this._debugSessionRequest({ debugRequest, debugAction: IFrameDebugAction.ExecuteSession });
    }

    public async executeDataFlowDebugCommand<T>(
        debugRequest: IExecuteDebugCommandPayload<T>
    ): Promise<IExecuteDataFlowDebugQueryResponseWrapper> {
        return this._debugSessionRequest({ debugRequest, debugAction: IFrameDebugAction.ExecuteCommand });
    }

    public async executePipelineRunCommand(
        request: IIFrameCreatePipelineRunRequestData
    ): Promise<IPipelineRunResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.HostRequestPipelineRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage) => {
            return (message.data && (message.data as IIFrameCreatePipelineRunResponseData)).result;
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeCopyJobRunCommand(request: IIFrameCreateCopyJobRunRequestData): Promise<ICopyJobRunResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.HostRequestCopyJobRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage) => {
            return (message.data && (message.data as IIFrameCreateCopyJobRunResponseData)).result;
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeSaveScheduledJobsCommand(
        request: IIFrameSaveScheduledJobsRequestData
    ): Promise<IScheduledJobsResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.HostRequestSaveScheduledJobs,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage) => {
            return (message.data && (message.data as IIFrameSaveScheduledJobsResponseData)).result;
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async createStorageTrigger(
        request: IIFrameCreateStorageTriggerRequestData
    ): Promise<IIFrameCreateStorageTriggerResponseData> {
        const hostMessage = {
            type: TridentIFrameMessageType.CreateStorageTrigger,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameCreateStorageTriggerResponseData>) => {
            if (message.error) {
                return { success: false, error: message.error };
            }
            return message.data ?? { success: false, error: 'No response data' };
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async createCopyJobOneLakeEventTrigger(
        request: IIFrameCreateCopyJobOneLakeEventTriggerRequestData
    ): Promise<IIFrameCreateStorageTriggerResponseData> {
        const hostMessage = {
            type: TridentIFrameMessageType.CreateCopyJobOneLakeEventTrigger,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameCreateStorageTriggerResponseData>) => {
            if (message.error) {
                return { success: false, error: message.error };
            }
            return message.data ?? { success: false, error: 'No response data' };
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async openGetDataWizard(request: IIFrameOpenGetDataRequestData) {
        const hostMessage = {
            type: TridentIFrameMessageType.HostRequestOpenGetData,
            data: request
        };
        return this.requestMessage(hostMessage);
    }

    public async executeQueryPipelineRunCommand<T>(
        request: IIFrameQueryPipelineRunRequestData
    ): Promise<Model.PipelineRun> {
        const hostMessage = {
            type: TridentIFrameMessageType.QueryPipelineRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage) => {
            return (message.data && (message.data as IIFrameQueryPipelineRunResponseData)).result;
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeQueryActivityRunsCommand(
        request: IIFrameQueryActivityRunsRequestData
    ): Promise<Model.ActivityRunsListResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.QueryActivityRuns,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameQueryActivityRunsResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeQueryDbtItemRunsCommand(
        request: IIFrameQueryDbtItemRunsRequestData
    ): Promise<Model.ActivityRunsListResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.QueryDbtItemRuns,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameQueryActivityRunsResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeQueryCopyJobRunCommand<T>(
        request: IIFrameQueryCopyJobRunRequestData
    ): Promise<Model.CopyJobRun> {
        const hostMessage = {
            type: TridentIFrameMessageType.QueryCopyJobRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage) => {
            return (message.data && (message.data as IIFrameQueryCopyJobRunResponseData)).result;
        };
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeQueryCopyJobActivityRunsCommand(
        request: IIFrameQueryCopyJobRunsRequestData
    ): Promise<Model.ActivityRunsListResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.QueryCopyJobRuns,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameQueryActivityRunsResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeListPipelineRunsCommand(
        request: IIFrameQueryPipelineRunsRequestData
    ): Promise<Model.PipelineRunsQueryResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.QueryPipelineRuns,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameQueryPipelineRunsResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeGetActivityRunProgressCommand(
        request: IIFrameGetActivityRunProgressRequestData
    ): Promise<IActivityRunProgress> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetActivityRunProgress,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetActivityRunProgressResponseData>) =>
            message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeGetCopyJobActivityRunProgressCommand(
        request: IIFrameQueryCopyJobActivityRunProgressRequestData
    ): Promise<ICopyJobActivityRunProgress | ICopyJobActivityRunProgress[] | undefined> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetCopyJobActivityRunProgress,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetCopyJobActivityRunProgressResponseData>) =>
            message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executePushJobStateCommand(request: IIFramePushJobStateRequestData): Promise<unknown> {
        const hostMessage = {
            type: TridentIFrameMessageType.PushJobState,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<unknown>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeGetLibraryVariablesCommand(
        request: IIFrameGetLibraryVariablesRequestData
    ): Promise<IDiscoveredVariables> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetLibraryVariables,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetLibraryVariablesResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeVariablePickerCommand(
        request: IIFrameTridentWorkspaceBaseData
    ): Promise<IIFrameOpenLibraryVariablePickerResponseData> {
        const hostMessage = {
            type: TridentIFrameMessageType.OpenLibraryVariablePicker,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameOpenLibraryVariablePickerResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeGetReflexTriggersCommand(
        request: IIFrameGetTriggersRequestData
    ): Promise<IReflexTriggerData[]> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetTriggers,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetTriggersResponseData>) => message.data?.results;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeNewReflexTriggerCommand(request: IIFrameGetTriggersRequestData): Promise<void> {
        const hostMessage = {
            type: TridentIFrameMessageType.NewTrigger,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<void>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeEditReflexTriggerCommand(request: IIFrameEditTriggerRequestData): Promise<void> {
        const hostMessage = {
            type: TridentIFrameMessageType.EditTrigger,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<void>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeCancelPipelineRunCommand(
        request: IIFrameCancelPipelineRunRequestData
    ): Promise<IIFrameCancelPipelineRunResponseData> {
        const hostMessage = {
            type: TridentIFrameMessageType.CancelPipelineRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameCancelPipelineRunResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeCancelCopyJobRunCommand(request: IIFrameCancelCopyJobRunRequestData): Promise<unknown> {
        const hostMessage = {
            type: TridentIFrameMessageType.CancelCopyJobRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameCancelCopyJobRunResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeRerunPipelineCommand(
        request: IIFrameRerunPipelineRequestData
    ): Promise<Model.CreateRunResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.RerunPipeline,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameRerunPipelineResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeResetCopyJobCommand(
        request: IIFrameResetCopyJobRequestData
    ): Promise<IIFrameMessage<IIFrameResetCopyJobResponseData>> {
        const hostMessage = {
            type: TridentIFrameMessageType.ResetCopyJob,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameResetCopyJobResponseData>) => message;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async executeApproveActivityRunCommand(
        request: IIFrameApproveActivityRunRequestData
    ): Promise<Model.ApproveActivityRunResponse> {
        const hostMessage = {
            type: TridentIFrameMessageType.ApproveActivityRun,
            data: request
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameApproveActivityRunResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async sendResourceCodeAsync(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resource: any,
        resourceType: string,
        id: string = this._saveId
    ) {
        const hostMessage: IIFrameMessage = {
            id,
            type: CommonIFrameMessageType.ResourceCode,
            data: {
                resourceName: resource.name || resource.displayName,
                resourceType,
                resource
            }
        };
        return this.requestMessage(hostMessage);
    }

    public async getResourceAsync(options: IIFrameGetResourcesRequestData): Promise<IAzureResource | ITridentResource> {
        const resources = await this._getResources(options);
        return resources[0];
    }

    public async saveResourceInDedicatedIframeAsync(
        resource: { name?: string; displayName?: string },
        resourceType: string,
        id: string = this._saveId
    ) {
        const hostMessage: IIFrameMessage<IIFrameResourceCodeData> = {
            id,
            type: CommonIFrameMessageType.ResourceCode,
            data: {
                contextData: undefined,
                resourceObjectID: undefined,
                workspaceObjectId: undefined,
                resourceName: resource.name || resource.displayName,
                resourceType,
                resource,
                save: true
            }
        };
        return this.requestMessage(hostMessage, (message: IIFrameMessage<IIFrameSaveResourceResponseData<boolean>>) => {
            this.postSave();
            return message.data?.saved;
        });
    }

    public async saveResourceInTridentAsync(
        contextData: IResourceContext,
        resource: { objectId?: string; displayName?: string },
        resourceType: string,
        operation?: 'create' | 'update'
    ) {
        const hostMessage: IIFrameMessage<IIFrameSaveResourceRequestData> = {
            type: TridentIFrameMessageType.SaveResource,
            data: {
                contextData,
                resourceObjectID: resource.objectId,
                workspaceObjectId: contextData.contextId,
                resourceName: resource.displayName,
                resourceType,
                resource,
                operation
            }
        };
        return this.requestMessage(
            hostMessage,
            (message: IIFrameMessage<IIFrameSaveResourceResponseData<IIFrameSaveTridentResourceData>>) => {
                return message.data?.saved;
            }
        );
    }

    public async openSubfolderPickerDialogAsync(
        dialogTitle: string,
        confirmButtonText: string,
        showNewButton?: boolean
    ) {
        const hostMessage: IIFrameMessage<OpenSubfolderPickerDialogConfig> = {
            type: TridentIFrameMessageType.OpenSubfolderPickerDialog,
            data: {
                title: dialogTitle,
                confirmButtonText,
                showNewButton
            }
        };
        return this.requestMessage(hostMessage, (message: IIFrameMessage<OpenSubfolderPickerDialogResult>) => {
            return message.data?.value;
        });
    }

    public async deleteResourceAsync(data: IIFrameDeleteResourceRequestData): Promise<IResponseOptionsArgs> {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.DeleteResource,
            data
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameHttpResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async listResourcesAsync(options: IIFrameGetResourcesRequestData) {
        return this._getResources(options);
    }

    public async exportScript(scriptFragment: string, id?: string): Promise<void> {
        const hostMessage = {
            id,
            type: CommonIFrameMessageType.ExportScript,
            data: {
                scriptFragment
            }
        };
        this._postIframeMessage(hostMessage);
    }

    public createGetData(result: ICopyAssistantDeploymentPayload): void {
        this._requestGetDataModelCreation$.next(result);
    }

    public createTemplate(result: ITemplateDeploymentPayload): void {
        this._requestTemplateModelCreation$.next(result);
    }

    public createCopyJob(result: {
        payload: ICopyJobDeploymentPayload;
        resolve: () => void;
        reject: (reason?: unknown) => void;
    }): void {
        this._requestCopyJobCreation$.next(result);
    }

    public quickRefreshTab(data: IIFrameResourceActionBaseData): void {
        this._requestQuickRefreshTab$.next(data);
    }

    public openActivityRuns(data: IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenMonitoringPageRequestData>) {
        this._requestActivityRuns$.next(data);
    }

    public openCopyJobRun(data: IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenCopyJobRunPageRequestData>) {
        this._requestCopyJobRun$.next(data);
    }

    public openResourceTab(data: IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenResourceInTridentWsData>) {
        this._requestOpenResource$.next(data);
    }

    public openMountedDataFactory(data: IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenMountedDataFactory>) {
        this._requestOpenMountedDataFactory$.next(data);
    }

    public closeResourceTab(data?: IIFrameMessage<IIFrameResourceActionBaseData>) {
        this._requestCloseResource$.next(data);
    }

    public triggerRun(data?: IIFrameTriggerRunData) {
        this._requestTriggerRun$.next(data);
    }

    public updateTab(data: IIFrameUpdateTridentTabData): void {
        const hostMessage = {
            type: TridentIFrameMessageType.UpdateTab,
            data
        };
        this.requestMessage(hostMessage);
    }

    public updateCopilotSession(data: IIFrameUpdateCopilotSessionData): void {
        const hostMessage = {
            type: TridentIFrameMessageType.UpdateCopilotSession,
            data
        };
        this.requestMessage(hostMessage);
    }

    public async exportExpression(expression: string, id?: string): Promise<void> {
        const hostMessage = {
            id,
            type: CommonIFrameMessageType.ExportExpression,
            data: {
                expression
            }
        };
        this._postIframeMessage(hostMessage);
    }

    public openPanel(uiOptions: IIFrameOpenPanelRequestData): Promise<undefined> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.OpenPanel,
                data: uiOptions
            },
            (message: IIFrameMessage<undefined>) => {
                return message.data;
            }
        );
    }

    public getConnectionAuthoringLink(
        data: IIFrameGetConnectionAuthoringLinkRequestData
    ): Promise<IIFrameGetExternalPortalLinkResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetConnectionAuthoringLink,
                data
            },
            (message: IIFrameMessage<IIFrameGetExternalPortalLinkResponseData>) => message.data
        );
    }

    public getExtensionHostOriginLink(): Promise<IIFrameGetExternalPortalLinkResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetExtensionHostOriginLink
            },
            (message: IIFrameMessage<IIFrameGetExternalPortalLinkResponseData>) => message.data
        );
    }

    public getMonitorBasePath(): Promise<string> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetMonitorBasePath
            },
            (message: IIFrameMessage<string>) => message.data
        );
    }

    public getArtifactJobHistory(data: IIFrameFetchJobHistoryRequestData): Promise<ArtifactJobHistory> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.FetchJobHistory,
                data
            },
            (message: IIFrameMessage<ArtifactJobHistory>) => message.data
        );
    }

    public getArtifactJobInstance(data: IIFrameFetchJobInstanceRequestData): Promise<ArtifactJobInstance> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.FetchJobInstance,
                data
            },
            (message: IIFrameMessage<ArtifactJobInstance>) => message.data
        );
    }

    public listArtifactSchedules(data: IIFrameScheduleMessageData): Promise<ArtifactSchedule[]> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.FetchSchedules,
                data
            },
            (message: IIFrameMessage<ArtifactSchedule[]>) => message.data
        );
    }

    public closePanel(data: IIFrameCloseSideNavRequestData): void {
        this.requestMessage({
            type: TridentIFrameMessageType.ClosePanel,
            data
        });
    }

    public openDialog(opts?: IIFrameOpenDialogRequestData & IIFrameResourceContextBase): Promise<undefined> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.OpenDialog,
                data: opts
            },
            (message: IIFrameMessage<undefined>) => message.data
        );
    }

    public closeDialog(): Promise<void> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CloseDialog
            },
            noop
        );
    }

    public saveCurrentArtifact() {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.SaveCurrentArtifact
            },
            (message: IIFrameMessage<boolean>) => message.data
        );
    }

    public navigate(data: IIFrameNavigateRequestData): void {
        this.requestMessage({
            type: TridentIFrameMessageType.Navigate,
            data
        });
    }

    public openPage(data: IIFrameOpenPageRequestData): void {
        this.requestMessage({
            type: TridentIFrameMessageType.OpenPage,
            data
        });
    }

    public async createPipeline(data: IIFrameCreatePipelineRequestData): Promise<string> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CreatePipeline,
                data
            },
            (message: IIFrameMessage<{ objectId: string }>) => message.data?.objectId
        );
    }

    public openNotification(
        config: Pick<shellapi.notifications.IToastConfig, 'mode' | 'header' | 'message' | 'duration'>
    ): void {
        this.requestMessage({
            type: TridentIFrameMessageType.OpenNotification,
            data: config
        });
    }

    public hideNotification(config: { id: number }): void {
        this.requestMessage({
            type: TridentIFrameMessageType.DismissNotification,
            data: config
        });
    }

    public async getAccessToken(
        area: string | string[],
        forceRefresh?: boolean,
        account?: AccountInfo,
        tenantId?: string
    ): Promise<string> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetToken,
            data: {
                area,
                forceRefresh,
                tenantId,
                account
            }
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetTokenResultData>) => message.data?.accessToken;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async getAllAccounts(): Promise<AccountInfo[]> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetAllAccounts
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetAllAccountsResultData>) => message.data?.accounts;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async loginPopUp(tenantId?: string, prompt: PromptValue = 'select_account'): Promise<AccountInfo> {
        const hostMessage = {
            type: TridentIFrameMessageType.LoginPopup,
            data: { tenantId, prompt }
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameLoginPopupResultData>) => message.data?.account;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async getUserProfile(): Promise<IIFrameGetUserProfileResultData> {
        const hostMessage = {
            type: TridentIFrameMessageType.GetUserProfile
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetUserProfileResultData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async execExploratoryAPI(data: IRequestOptionsArgs): Promise<IResponseOptionsArgs> {
        const hostMessage: IIFrameMessage<IRequestOptionsArgs> = {
            type: TridentIFrameMessageType.ExecExploratoryAPI,
            data
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameHttpResponseData>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async getEventStreamArtifactModel(
        data: IIFrameGetEventStreamArtifactModelRequestData
    ): Promise<IIFrameGetEventStreamArtifactModelResponseData> {
        const hostMessage: IIFrameMessage<IIFrameGetEventStreamArtifactModelRequestData> = {
            type: TridentIFrameMessageType.GetEventStreamArtifactModel,
            data
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetEventStreamArtifactModelResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async previewEventStream(
        data: IIFramePreviewEventStreamRequestData
    ): Promise<Array<Record<string, unknown>>> {
        const hostMessage: IIFrameMessage<IIFramePreviewEventStreamRequestData> = {
            type: TridentIFrameMessageType.PreviewEventStream,
            data
        };
        const iframeMessage = (message: IIFrameMessage<Array<Record<string, unknown>>>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestGetConnectors(data: IIFrameTridentWorkspaceBaseData) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.RequestLaGetConnectors,
            data
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<any>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestGetActions(data: IIFrameConnectorRequestData) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.RequestLaGetActions,
            data
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<any>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestGetTriggers(data: IIFrameConnectorRequestData) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.RequestLaGetTriggers,
            data
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<any>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestGetConnectorSwagger(data: IIFrameConnectorRequestData) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.RequestLaGetConnectorSwagger,
            data
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<any>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    // NOTE: anredd - This API does not actually exist don't use this call
    public async requestGetConnections(data: IIFrameTridentWorkspaceBaseData) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.RequestLaGetConnections,
            data
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<any>) => message.data?.result;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestLaCreateConnection(activityType: string, objectId: string): Promise<boolean> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.RequestLaCreateConnection,
                data: {
                    objectId,
                    activityType
                }
            },
            (message: IIFrameMessage<IIFrameLogicAppsAuthenticationResponseData>) => {
                return message.data?.authenticatedSuccessfully;
            }
        );
    }

    public async requestLaGetConnection(
        activityType: string,
        objectId: string,
        workspaceObjectId: string,
        useCredentialForPipelineOwner: boolean
    ) {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.RequestLaGetConnection,
                data: {
                    objectId,
                    activityType,
                    useCredentialForPipelineOwner,
                    workspaceObjectId
                }
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (message: IIFrameMessage<any>) => {
                return message.data?.properties;
            }
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async requestLaDynamicInvoke(data: IIFrameDynamicInvokeRequestData): Promise<any> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.RequestLaDynamicInvoke,
                data
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (message: IIFrameMessage<any>) => {
                return message.data;
            }
        );
    }

    public async sendLogicAppsActivityMessage(
        activityType: 'email' | 'teams' | 'azureblob' | 'azuredevops' | 'azuredatafactory',
        action: IFrameLogicAppsActivityAction,
        objectId: string
    ) {
        return this.requestMessage({
            type: TridentIFrameMessageType.LogicAppsActivityAction,
            data: {
                objectId,
                activityType,
                action
            }
        });
    }

    public async requestDataflowParameters(dataflowId: string, workspaceId: string) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.GetDataflowParameters,
            data: {
                dataflowId,
                workspaceId
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<IIFrameGetDataflowParametersResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestNotebookContent(notebookId: string, workspaceId: string) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.GetNotebookContent,
            data: {
                notebookId,
                workspaceId
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeMessage = (message: IIFrameMessage<IIFrameGetNotebookContentResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestLakehouseTables(lakehouseId: string, workspaceId: string, schema?: string) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.GetLakehouseTables,
            data: {
                lakehouseId,
                workspaceId,
                schema
            }
        };

        const iframeMessage = (message: IIFrameMessage<IIFrameGetLakehouseTablesResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestLakehouseSchemas(lakehouseId: string, workspaceId: string) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.GetLakehouseSchemas,
            data: {
                lakehouseId,
                workspaceId
            }
        };

        const iframeMessage = (message: IIFrameMessage<IIFrameGetLakehouseSchemasResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public async requestLakehouseSchemasEnabled(lakehouseId: string, workspaceId: string) {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.GetLakehouseSchemasEnabled,
            data: {
                lakehouseId,
                workspaceId
            }
        };

        const iframeMessage = (message: IIFrameMessage<IIFrameGetLakehouseSchemasEnabledResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public requestExportScript(id: string): void {
        this._requestExportScript$.next(id);
    }

    public postSave(): void {
        this._saveId = null;
    }

    public async updateResyncModelServiceStatus(msg: IIFrameMessage, data: IIFrameAsyncOperationResult): Promise<void> {
        this.requestMessage({
            ...msg,
            data
        });
    }

    public async returnDiscardAll(msg: IIFrameMessage): Promise<void> {
        this.requestMessage({
            ...msg
        });
    }

    public async updateValidationStatus(msg: IIFrameMessage, data: boolean): Promise<void> {
        this.requestMessage({
            ...msg,
            data
        });
    }

    public addActivity(data: IIFrameAddActivityData) {
        this._requestAddActivity$.next(data);
    }

    public addActivityJSON(message: IIFrameMessage<IIFrameExecuteCopilotAppActionData>) {
        this._requestAddActivityJSON$.next(message);
    }

    public zoomRequest(zoomLevel: IIFrameZoomOptionData) {
        this._requestZoom$.next(zoomLevel);
    }

    public refreshPipelineRun(run: IIFrameActivityRunData) {
        this._requestRefreshPipelineRun$.next(run);
    }

    public createArtifact(data: IIFrameCreateArtifactRequestData): Promise<ICreateArtifactResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CreateArtifact,
                data
            },
            (message: IIFrameMessage<ICreateArtifactResponseData>) => message.data
        );
    }

    public openArtifact(data: IIFrameOpenArtifactRequestData) {
        this.requestMessage({
            type: TridentIFrameMessageType.OpenArtifact,
            data
        });
    }

    public getFeatureEcValue(data: IIFrameGetECValueRequestData) {
        const hostMessage: IIFrameMessage<IIFrameGetECValueRequestData> = {
            type: TridentIFrameMessageType.GetECValues,
            data
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetECValueResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public sendCosmosDbWorkloadRequest(
        data: IIFrameCosmosDbWorkloadRequestData
    ): Promise<IIFrameCosmosDbWorkloadResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CosmosDbWorkloadRequest,
                data
            },
            (message: IIFrameMessage<IIFrameCosmosDbWorkloadResponseData>) => message.data
        );
    }

    /**
     * The way to figure out workspace private-link endpoints in madrid
     * @description Returned result for NetworkSecurityAPI.getWorkspaceEndpoints API.
     */
    public getWorkspaceEndpoints(
        data: IIFrameGetWorkspaceEndpointsRequestData
    ): Promise<IIFrameGetWorkspaceEndpointsResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetWorkspaceEndpoints,
                data
            },
            (message: IIFrameMessage<IIFrameGetWorkspaceEndpointsResponseData>) => message.data
        );
    }

    public getWorkspaceNetworkSecuritySettings(
        data: IIFrameGetWorkspaceNetworkSecuritySettingsRequestData
    ): Promise<IIFrameGetWorkspaceNetworkSecuritySettingsResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetWorkspaceNetworkSecuritySettings,
                data
            },
            (message: IIFrameMessage<IIFrameGetWorkspaceNetworkSecuritySettingsResponseData>) => message.data
        );
    }

    public getOneLakeApiEndpoint(): Promise<IIFrameGetOneLakeApiEndpointResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetOneLakeApiEndpoint
            },
            (message: IIFrameMessage<IIFrameGetOneLakeApiEndpointResponseData>) => message.data
        );
    }

    public callApiService<
        S extends TridentApiServiceName = TridentApiServiceName,
        M extends TridentApiServiceMethodName<S> = TridentApiServiceMethodName<S>,
        R extends TridentApiServiceMethodResults<S, M> = TridentApiServiceMethodResults<S, M>
    >(data: IIframeCallApiServiceRequestData<S>): Promise<R> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CallApiService,
                data
            },
            (message: IIFrameMessage<R>) => {
                if (message?.error) {
                    throw message.error;
                }
                return message?.data as R;
            }
        );
    }

    public checkCopilotAvailability(
        data: IIFrameCopilotAvailabilityRequestData
    ): Promise<IIFrameCopilotAvailabilityResponseData> {
        const hostMessage: IIFrameMessage<IIFrameCopilotAvailabilityRequestData> = {
            type: TridentIFrameMessageType.CheckCopilotAvailability,
            data
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameCopilotAvailabilityResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public checkFeedbackAvailability(
        data: IIFrameMessageData
    ): Promise<IIFrameFeedbackAvailabilityResponseData> {
        const hostMessage: IIFrameMessage<IIFrameMessageData> = {
            type: TridentIFrameMessageType.CheckFeedbackAvailability,
            data
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameFeedbackAvailabilityResponseData>) => message.data;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    public getRibbonButtonLocation(
        data: IIFrameGetRibbonButtonRectRequest
    ): Promise<IIFrameGetRibbonButtonLocationResponse> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.RequestGetRibbonButtonLocation,
                data
            },
            (message: IIFrameMessage<IIFrameGetRibbonButtonLocationResponse>) => message.data
        );
    }

    public alertModelServiceDoneFetching(): Promise<void> {
        const hostMessage: IIFrameMessage = {
            type: TridentIFrameMessageType.ModelServiceDoneFetching,
        };
        return this.requestMessage(hostMessage);
    }

    public alertMdfDirtyStateChanged(resource: Model.DataFlow, resourceObjectID: string, isDirty: boolean): void {
        try {
            const hostMessage: IIFrameMessage = {
                type: TridentIFrameMessageType.MdfDirtyStateChanged,
                data: {
                    resourceObjectID,
                    resource: JSON.parse(JSON.stringify(resource)),
                    isDirty
                }
            };
            this._postIframeMessage(hostMessage);
        } catch (error) {
            this._loggerService.error(
                IFrameMessengerService.className,
                'alertMdfDirtyStateChanged - failed to serialize resource',
                error
            );
        }
    }

    public getConnectionFromGetDataExperience(data: IOpenUnifiedGetData): Promise<ICloseDialogResult<ICloseUnifiedGetData>> {
        const hostMessage: IIFrameMessage<IOpenUnifiedGetData> = {
            type: TridentIFrameMessageType.GetConnectionViaGetDataExperience,
            data
        };

        const iframeMessage = (message: IIFrameMessage<{
            data: ICloseDialogResult<ICloseUnifiedGetData>
        }>) => {
            return message.data.data;
        };

        return this.requestMessage(hostMessage, iframeMessage);
    }

    public editConnectionFromGetDataExperience(data: IOpenUnifiedEditConnection): Promise<ICloseDialogResult<ICloseUnifiedEditConnection>> {
        const hostMessage: IIFrameMessage<IOpenUnifiedEditConnection> = {
            type: TridentIFrameMessageType.EditConnectionViaGetDataExperience,
            data
        };

        const iframeMessage = (message: IIFrameMessage<{
            data: ICloseDialogResult<ICloseUnifiedEditConnection>
        }>) => {
            return message?.data?.data;
        };

        return this.requestMessage(hostMessage, iframeMessage);
    }

    public createDataPipeline(
        data: IIFrameCreateDataPipelineRequestData
    ): Promise<IIFrameCreateDataPipelineResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CreateDataPipeline,
                data
            },
            (message: IIFrameMessage<IIFrameCreateDataPipelineResponseData>) => message.data
        );
    }

    public createTumblingWindowTrigger(
        data: IIFrameCreateTumblingWindowTriggerRequestData
    ): Promise<IIFrameCreateTumblingWindowTriggerResponseData> {
        if (!GlobalVariables.fabricTwt) {
            return Promise.reject(new Error(stringResources.TumblingWindowTrigger_CreationDisabled));
        }

        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CreateTumblingWindowTrigger,
                data
            },
            (message: IIFrameMessage<IIFrameCreateTumblingWindowTriggerResponseData>) => message.data
        );
    }

    public getTumblingWindowTrigger(
        data: IIFrameGetTumblingWindowTriggerRequestData
    ): Promise<IIFrameGetTumblingWindowTriggerResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.GetTumblingWindowTrigger,
                data
            },
            (message: IIFrameMessage<IIFrameGetTumblingWindowTriggerResponseData>) => message.data
        );
    }

    public updateTumblingWindowTrigger(
        data: IIFrameUpdateTumblingWindowTriggerRequestData
    ): Promise<IIFrameUpdateTumblingWindowTriggerResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.UpdateTumblingWindowTrigger,
                data
            },
            (message: IIFrameMessage<IIFrameUpdateTumblingWindowTriggerResponseData>) => message.data
        );
    }

    public deleteTumblingWindowTrigger(
        data: IIFrameDeleteTumblingWindowTriggerRequestData
    ): Promise<IIFrameTumblingWindowTriggerOperationResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.DeleteTumblingWindowTrigger,
                data
            },
            (message: IIFrameMessage<IIFrameTumblingWindowTriggerOperationResponseData>) => message.data
        );
    }

    public startTumblingWindowTrigger(
        data: IIFrameTumblingWindowTriggerRuntimeRequestData
    ): Promise<IIFrameTumblingWindowTriggerOperationResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.StartTumblingWindowTrigger,
                data
            },
            (message: IIFrameMessage<IIFrameTumblingWindowTriggerOperationResponseData>) => message.data
        );
    }

    public stopTumblingWindowTrigger(
        data: IIFrameTumblingWindowTriggerRuntimeRequestData
    ): Promise<IIFrameTumblingWindowTriggerOperationResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.StopTumblingWindowTrigger,
                data
            },
            (message: IIFrameMessage<IIFrameTumblingWindowTriggerOperationResponseData>) => message.data
        );
    }

    public queryTumblingWindowTriggers(
        data: IIFrameQueryTumblingWindowTriggersRequestData
    ): Promise<ITumblingWindowTriggerListArtifactData[]> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.QueryTumblingWindowTriggers,
                data
            },
            (message: IIFrameMessage<IIFrameQueryTumblingWindowTriggersResponseData>) => message.data?.result
        );
    }

    public updateDataPipeline(
        data: IIFrameUpdateDataPipelineDefinitionRequestData
    ): Promise<IIFrameUpdateDataPipelineDefinitionResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.UpdateDataPipeline,
                data
            },
            (message: IIFrameMessage<IIFrameUpdateDataPipelineDefinitionResponseData>) => message.data
        );
    }

    public createDataflowFabric(
        data: IIFrameCreateDataflowFabricRequestData
    ): Promise<IIFrameCreateDataflowFabricResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CreateDataflowFabric,
                data
            },
            (message: IIFrameMessage<IIFrameCreateDataflowFabricResponseData>) => message.data
        );
    }

    public updateDataflowFabric(
        data: IIFrameUpdateDataflowFabricDefinitionRequestData
    ): Promise<IIFrameUpdateDataflowFabricDefinitionResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.UpdateDataflowFabric,
                data
            },
            (message: IIFrameMessage<IIFrameUpdateDataflowFabricDefinitionResponseData>) => message.data
        );
    }

    public createFolder(
        data: IIFrameCreateFolderRequestData
    ): Promise<IIFrameCreateFolderResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.CreateFolder,
                data
            },
            (message: IIFrameMessage<IIFrameCreateFolderResponseData>) => message.data
        );
    }

    public listFolders(
        data: IIFrameListFoldersRequestData
    ): Promise<IIFrameListFoldersResponseData> {
        return this.requestMessage(
            {
                type: TridentIFrameMessageType.ListFolders,
                data
            },
            (message: IIFrameMessage<IIFrameListFoldersResponseData>) => message.data
        );
    }

    public async requestMessage<D = unknown, T = unknown, R = unknown>(
        hostMessage: IIFrameMessage<D>,
        resolveIFrameMessage?: (message: IIFrameMessage<T>) => R | undefined
    ): Promise<R> {
        await this._initializedPromise;
        hostMessage.id = hostMessage.id || this._generateId();
        const id = hostMessage.id;

        // okay, this.message$ is responsible for the incoming messages from trident
        const promise = this.message$
            .pipe(
                filter((e) => e.type === hostMessage.type && e.id === id),
                take(1)
            )
            .toPromise();

        // this is the right place to post the message
        this._postIframeMessage(hostMessage);
        if (resolveIFrameMessage) {
            return new Promise((resolve, reject) => {
                promise.then((message: IIFrameMessage<T>) => {
                    if (message.type === hostMessage.type) {
                        if (message.error != null) {
                            reject(message.error);
                            this._loggerService.error(
                                IFrameMessengerService.className,
                                'resolveIFrameMessage  - failed',
                                {
                                    type: message.type,
                                    id: message.id,
                                    error: message.error
                                }
                            );
                        } else {
                            resolve(resolveIFrameMessage(message));
                            this._loggerService.log(
                                IFrameMessengerService.className,
                                'resolveIFrameMessage  - resolved',
                                {
                                    type: message.type,
                                    id: message.id
                                }
                            );
                        }
                    }
                });
            });
        }
        return null;
    }

    private _getResources(options: IIFrameGetResourcesRequestData) {
        const {
            contextData,
            resourceType,
            resourceName,
            resourceObjectID,
            shallowGet,
            resourceSubType,
            workspaceObjectId,
            forceRefresh
        } = options;
        const hostMessage: IIFrameMessage<IIFrameGetResourcesRequestData> = {
            type: CommonIFrameMessageType.GetResources,
            data: {
                contextData,
                workspaceObjectId,
                resourceType: `${resourceType}s`,
                resourceName,
                resourceObjectID,
                shallowGet,
                resourceSubType,
                forceRefresh
            }
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameGetResourcesResponseData>) => message.data?.resources;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    private _postIframeMessage<T>(message: IIFrameMessage<T>): void {
        message.sessionId = this._sessionId;
        window.parent.postMessage(message, this._parentOrigin || '*');
    }

    private _generateId(): string {
        const id = '' + this._nextId;
        this._nextId += 1;
        return id;
    }

    private async _listMetadata(message: IIFrameMessage): Promise<void> {
        const { contextData, resourceType } = message.data as IIFrameListResourcesMessageData;
        let data;
        switch (resourceType) {
            case 'linkedService': {
                const lsRegistryService = await this._getServiceInstance<LinkedServiceRegistryService>(
                    contextData,
                    'LinkedServiceRegistryService'
                );
                const lsMetadataList = lsRegistryService.getAllLinkedServiceConfigurations();
                data = JSON.parse(JSON.stringify(lsMetadataList));
                break;
            }
            default:
                break;
        }

        this.requestMessage({
            ...message,
            data
        });
    }

    private async _listResources(message: IIFrameMessage<IIFrameListResourcesMessageData>): Promise<void> {
        const { contextData, resourceType, resourceObjectID } = message.data as IIFrameListResourcesMessageData;
        const modelService = await this._getServiceInstance<ModelService>(contextData, 'ModelService');
        try {
            const nameds = (
                (await firstValueFrom(
                    modelService
                        .list(undefined, Metamodel.modelByName(resourceType))
                        .pipe(timeout({ first: 6000, with: () => from([]) })),
                    { defaultValue: [] }
                )) as Named[]
            ).filter(
                (item) =>
                    (!(item instanceof LinkedService) || !item.isFakeEntityCreatedFromReference) &&
                    (!resourceObjectID || resourceObjectID === item.objectId)
            );
            const resources = [];
            for (const named of nameds) {
                const resource = (await ModelUtilties.toResource(
                    AdapterContext.defaultContextOf(contextData),
                    named
                ).catch(
                    (e) => {
                        this._loggerService.warn(
                            IFrameMessengerService.className,
                            `ModelUtilties.toResource failed in _listResources, error: ${convertObjectToString(e)}`,
                            {
                                objectId: named.objectId,
                                name: named.name
                            }
                        );
                        return undefined;
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                )) as any;
                if (resource) {
                    if (resource?.properties?.extraTridentInfo) {
                        delete resource.properties.extraTridentInfo;
                    }
                    if (resource?.workspaceObjectId) {
                        delete resource.workspaceObjectId;
                    }
                    resources.push(resource);
                }
            }

            this.requestMessage({
                ...message,
                data: resources
            });
        } catch (error) {
            this._loggerService.warn(
                IFrameMessengerService.className,
                `_listResources - failed, error: ${convertObjectToString(error)}`,
                {
                    ...contextData,
                    resourceType,
                    resourceObjectID
                }
            );
            this.requestMessage({
                ...message,
                data: [],
                error
            });
        }
    }

    private _getStyleFileUrl(message: IIFrameMessage) {
        const links = document.querySelectorAll('link[rel="stylesheet"][href^="sprite."]');
        const urls = Array.from(links).map((link) => {
            const path = link.getAttribute('href');
            return path;
        });

        this.requestMessage({
            ...message,
            data: urls
        });
    }

    private async _resynchronizeResourcePerType(
        message: IIFrameMessage<IIFrameResynchronizeModelServiceResourceRequestData>
    ): Promise<void> {
        const { contextData, resourceType } = message.data;
        const modelService = await this._getServiceInstance<ModelService>(contextData, 'ModelService');
        await modelService.resynchronizeResource(resourceType as ResourceType);
        // post back message to indicate that the resynchronization is done
        this.requestMessage({
            ...message,
            data: undefined
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _debugSessionRequest(debugRequest: IIFrameDebugRequestData): Promise<any> {
        const hostMessage = {
            type: CommonIFrameMessageType.DebugSession,
            data: debugRequest
        };
        const iframeMessage = (message: IIFrameMessage<IIFrameDebugActionData>) =>
            message.data?.debugSessionResponse || message.data?.debugSessionRespone;
        return this.requestMessage(hostMessage, iframeMessage);
    }

    private async _initializeIFrame(): Promise<void> {
        if (__IS_FABRIC_DPE__ || __IS_FABRIC_ADF__) {
            this._loggerService.log(IFrameMessengerService.className, `_initializeIFrame - start`);
        }
        window.addEventListener(
            'message',
            (event) => {
                const message = event.data as IIFrameMessage;
                if (message.type === CommonIFrameMessageType.Initialize) {
                    this._parentOrigin = event.origin;
                }
                // theoretically we should ensure defined message type for all iframe apps, but we get no idea if dedicatedIframeApp is incorrectly relying on undefined type now as their logging is not visible to us.
                // so we ensure it for trident ws and mount df only now
                if (
                    event.origin === this._parentOrigin &&
                    (message.type != null || !(__IS_FABRIC_DPE__ || __IS_FABRIC_ADF__ || __IS_FABRIC_MDF__))
                ) {
                    this._handleNewMessage(message);
                }
            },
            false
        );

        if (isTridentIframeType('page')) {
            window.addEventListener('click', () => {
                const message = {
                    type: TridentIFrameMessageType.ClickEvent
                };
                this.requestMessage(message);
            });
            window.addEventListener('keydown', (e) => {
                const { ctrlKey, altKey, shiftKey, keyCode, key } = e;
                this.requestMessage({
                    type: TridentIFrameMessageType.KeyDownEvent,
                    data: { ctrlKey, altKey, shiftKey, keyCode, key }
                });
            });
        }

        const hostMessage = {
            type: CommonIFrameMessageType.Initialize
        };
        this._postIframeMessage(hostMessage);
        if (__IS_FABRIC_DPE__ || __IS_FABRIC_ADF__) {
            this._loggerService.log(IFrameMessengerService.className, `_initializeIFrame - end`);
        }
    }

    private async _repopulateResources(
        message: IIFrameMessage<IIFrameOpenResourceInDedicatedIFrameData>
    ): Promise<void> {
        const modelService = this._injector.get('ModelService') as ModelService;
        await modelService.resynchronize().then(() =>
            this._requestOpenResource$.next({
                contextData: undefined,
                ...message
            })
        );
    }

    private _checkIFrameProfile(): boolean {
        const hasCustomer = /[\?&]feature\.iFrameCustomer=([a-z]+)/i.test(window.location.href);
        if (hasCustomer) {
            const customerId = window.location.href.match(/[\?&]feature\.iFrameCustomer=([a-z]+)/i)[1] as IFrameProfile;
            return IFrameProfile.KPMGWB === customerId;
        }
        return false;
    }

    private async _handleNewMessage(message: IIFrameMessage): Promise<void> {
        if (__IS_FABRIC_DPE__ || __IS_FABRIC_ADF__) {
            const payload = this._getLoggablePayload(message);
            this._loggerService.log('IFrameMessengerService', `Handle received iframe message. Type: ${message.type}`, {
                type: message.type,
                ...(payload ? { messagePayload: JSON.stringify(payload) } : {})
            });
        }
        switch (message.type) {
            // #region Shared among dedicated and Trident iframe
            case CommonIFrameMessageType.OpenResource:
                {
                    if (__IS_FABRIC_DPE__) {
                        queueCommand<IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenResourceInTridentWsData>>({
                            executor: 'page',
                            commandType: 'openResource',
                            payload: {
                                contextData: (message as IIFrameMessage<IIFrameOpenResourceInTridentWsData>).data
                                    .contextData,
                                ...(message as IIFrameMessage<IIFrameOpenResourceInTridentWsData>)
                            }
                        });
                    } else {
                        if (this._checkIFrameProfile()) {
                            this._repopulateResources(
                                message as IIFrameMessage<IIFrameOpenResourceInDedicatedIFrameData>
                            );
                        } else {
                            this._requestOpenResource$.next({
                                contextData: undefined,
                                ...(message as IIFrameMessage<IIFrameOpenResourceData>)
                            });
                        }
                    }
                }
                break;
            case CommonIFrameMessageType.Refresh:
                this._requestRefresh$.next();
                break;
            case CommonIFrameMessageType.RequestResourceCode:
                const resourceCodeMessage = message as IIFrameMessage<IIFrameResourceCodeData>;
                if (resourceCodeMessage.data.save) {
                    if (!this._saveId) {
                        this._saveId = message.id;
                        this._requestSave$.next(resourceCodeMessage);
                    }
                } else {
                    this._requestCode$.next(message.id);
                }
                break;
            case CommonIFrameMessageType.InsertScript:
                const insertScriptData = message.data as IIFrameScriptData;
                this._requestInsertScript$.next(insertScriptData);
                break;
            case CommonIFrameMessageType.RequestExportScript:
                this.requestExportScript(message.id);
                break;
            case CommonIFrameMessageType.RequestExportExpression:
                this._requestExportExpression$.next(message.id);
                break;
            case CommonIFrameMessageType.RequestImportExpression:
                const messageData = {
                    data: message.data,
                    correlationId: message.correlationId
                };
                this._requestImportExpression$.next(messageData);
                break;
            case CommonIFrameMessageType.LoadMonitoring:
                if ((message.data as IIFrameLoadMonitoringData)?.activityOutput) {
                    this._requestLoadMonitoring$.next((message.data as IIFrameLoadMonitoringData).activityOutput);
                }
                break;
            // #endregion
            // #region Trident iframe only
            case TridentIFrameMessageType.RequestSaveResource:
                this._requestSave$.next(message as IIFrameMessage<IIFrameSaveResourceRequestData>);
                break;
            case TridentIFrameMessageType.RequestTriggerRun:
                this.triggerRun(message.data as IIFrameResourceActionBaseData);
                break;
            case TridentIFrameMessageType.OpenCreateTumblingWindowTrigger:
                if (GlobalVariables.fabricTwt) {
                    this._requestOpenCreateTumblingWindowTrigger$.next(
                        message as IIFrameMessage<IIFrameOpenCreateTumblingWindowTriggerRequestData>
                    );
                }
                break;
            case TridentIFrameMessageType.OpenManageTumblingWindowTriggers:
                if (GlobalVariables.fabricTwt) {
                    this._requestOpenManageTumblingWindowTriggers$.next(
                        message as IIFrameMessage<IIFrameOpenManageTumblingWindowTriggersRequestData>
                    );
                }
                break;
            case TridentIFrameMessageType.AddActivity:
                if ((message.data as IIFrameAddActivityData)?.modelName) {
                    this.addActivity(message.data as IIFrameAddActivityData);
                }
                break;
            case TridentIFrameMessageType.ExecuteCopilotAppAction:
                this.addActivityJSON(message as IIFrameMessage<IIFrameExecuteCopilotAppActionData>);
                break;
            case TridentIFrameMessageType.DetectChildPipeline:
                this._requestDetectChildPipeline$.next(message as IIFrameMessage<IIFrameResourceActionBaseData>);
                break;
            case TridentIFrameMessageType.DetectLogicAppsActivity:
                this._requestDetectLogicAppsActivity$.next(message as IIFrameMessage<IIFrameResourceActionBaseData>);
                break;
            case TridentIFrameMessageType.RequestValidate:
                this._requestValidate$.next(message as IIFrameMessage<IIFrameRequestValidateData>);
                break;
            case TridentIFrameMessageType.RequestPublishAll:
                this._requestPublishAll$.next(message as IIFrameMessage<IIFrameResourceActionBaseData>);
                break;
            case TridentIFrameMessageType.GetData: {
                const { flow, ...payload } = (message?.data || {}) as IIFrameGetDataRequestData;
                switch (flow) {
                    case 'pipeline.copy':
                        queueCommand<IIFrameOpenCopyAssistantData>({
                            executor: 'dialog',
                            commandType: 'openCopyAssistant',
                            payload: payload as IIFrameOpenCopyAssistantData
                        });
                        break;
                    case 'mount':
                        queueCommand<IIFrameOpenMountWizardData>({
                            executor: 'dialog',
                            commandType: 'openMountWizard',
                            payload: payload as IIFrameOpenMountWizardData
                        }).then((res) => {
                            this.requestMessage({
                                ...message,
                                data: res
                            });
                        });
                        break;
                    case 'copyjob':
                        queueCommand<IIFrameOpenCopyJobWizardData>({
                            executor: 'dialog',
                            commandType: 'openCopyJobWizard',
                            payload: payload as IIFrameOpenCopyJobWizardData
                        }).then((res) => {
                            this.requestMessage({
                                ...message,
                                data: res
                            });
                        }).catch((error) => {
                            this._loggerService.warn(
                                IFrameMessengerService.className,
                                `Failed to open CopyJob wizard, error: ${convertObjectToString(error)}`,
                                { flow: 'copyjob' }
                            );
                            this.requestMessage({
                                ...message,
                                data: undefined,
                                error: convertObjectToString(error, false)
                            });
                        });
                        break;
                    case 'dbt.wizard':
                        queueCommand<IIFrameOpenDbtJobWizardData>({
                            executor: 'dialog',
                            commandType: 'openDbtJobWizard',
                            payload: payload as IIFrameOpenDbtJobWizardData
                        }).then((res) => {
                            this.requestMessage({
                                ...message,
                                data: res
                            });
                        });
                        break;
                    case 'edit.fabric.connections':
                        queueCommand<IOpenUnifiedEditConnection, ICloseDialogResult<ICloseUnifiedEditConnection>>({
                            preparer: 'worker',
                            executor: 'dialog',
                            commandType: 'openUnifiedEditConnection',
                            payload: payload as IOpenUnifiedEditConnection
                        }).then((res) => {
                            this.requestMessage({
                                ...message,
                                data: {
                                    ...res,
                                    data: {
                                        ...res.data,
                                        linkedService: undefined,
                                        linkedServiceObjectId: res.data.linkedService.objectId,
                                        workspaceObjectId: res.data.linkedService.workspaceObjectId
                                    }
                                }
                            });
                        });
                        break;
                    case 'fabric.connections':
                        queueCommand<IIFrameOpenGetDataWizardData, ICloseDialogResult<ICloseUnifiedGetData>>({
                            preparer: 'worker',
                            executor: 'dialog',
                            commandType: 'openUnifiedGetData',
                            payload: payload as IIFrameOpenGetDataWizardData
                        }).then((res) => {
                            let responseData = res;
                            if (res.data?.resultType === 'linkedService') {
                                responseData = {
                                    ...res,
                                    data: {
                                        ...res.data,
                                        linkedService: undefined,
                                        linkedServiceObjectId: res.data.linkedService.objectId,
                                        workspaceObjectId: res.data.linkedService.workspaceObjectId
                                    }
                                }
                            }
                            this.requestMessage({
                                ...message,
                                data: responseData
                            });
                        });
                        break;
                    default:
                        this._loggerService.error(IFrameMessengerService.name, `Unknown getdata flow: ${flow}`);
                }
                break;
            }
            case TridentIFrameMessageType.GetECFeaturesList: {
                const { workspaceObjectId } = message.data as IIFrameGetECFeaturesListRequestData;
                this.requestMessage<IIFrameGetECFeaturesListResponseData>({
                    ...message,
                    data: {
                        featureList: tridentEcFeatures,
                        workspaceObjectId: workspaceObjectId
                    }
                });
                break;
            }
            case TridentIFrameMessageType.GetPipelineCopilotContext: {
                const { contextData, workspaceObjectId, resourceObjectID } =
                    message.data as IIFrameResourceActionBaseData;
                try {
                    const modelService = await this._getServiceInstance<ModelService>(contextData, 'ModelService');
                    let pipeline = await modelService.getNamedByObjectId<Pipeline>(resourceObjectID, 'pipeline');
                    if (!pipeline) {
                        pipeline = (await modelService.fetchByIdentity(
                            `pipeline$$${resourceObjectID}`,
                            true,
                            resourceObjectID,
                            { workspaceObjectId }
                        )) as Pipeline;
                    }
                    const pipelineCopilotContext = await generateCopilotContextFromPipelineModelAsync(pipeline, {
                        contextData,
                        workspaceObjectId,
                        resourceObjectID
                    });
                    this.requestMessage<IIFrameGetPipelineCopilotContextResponseData>({
                        ...message,
                        data: {
                            contextData,
                            resourceObjectID,
                            workspaceObjectId,
                            pipelineCopilotContext
                        }
                    });
                } catch (ex) {
                    this._loggerService.warn(
                        IFrameMessengerService.className,
                        `Failed to get pipeline copilot context, error: ${convertObjectToString(ex)}`,
                        { workspaceObjectId, resourceObjectID }
                    );
                    this.requestMessage({
                        ...message,
                        data: undefined,
                        error: convertObjectToString(ex, false)
                    });
                }
                break;
            }
            case TridentIFrameMessageType.OpenCodeEditor: {
                await queueCommand<IIFrameOpenJSONCodeRequestData>({
                    executor: 'dialog',
                    commandType: 'openCodeEditorDialog',
                    payload: message.data as IIFrameOpenJSONCodeRequestData
                });

                if ((message.data as IIFrameOpenJSONCodeRequestData)?.resourceType === 'copyjob') {
                    await sleep(0);
                    await queueCommand<boolean>({
                        executor: 'page',
                        commandType: 'saveCurrentArtifact'
                    });
                }
                break;
            }
            case TridentIFrameMessageType.RequestCancelRun:
                this._requestCancelRun$.next(message.data as IIFrameCancelPipelineRunRequestData);
                break;
            case TridentIFrameMessageType.RequestResetCopyJob:
                this._requestResetCopyJob$.next(message.data as IIFrameResourceActionBaseData);
                break;
            case TridentIFrameMessageType.RequestFetchSchedule:
                this._requestFetchSchedule$.next(message.data as IIFrameResourceObjectBase);
                break;
            case TridentIFrameMessageType.ResynchronizeModelService:
                this._requestResynchronizeModelService$.next(message as IIFrameMessage<IIFrameResourceContextBase>);
                break;
            case TridentIFrameMessageType.ResynchronizeModelServiceResource:
                this._resynchronizeResourcePerType(
                    message as IIFrameMessage<IIFrameResynchronizeModelServiceResourceRequestData>
                );
                break;
            case TridentIFrameMessageType.CloseTab:
                queueCommand<IIFrameMessage<IIFrameResourceActionBaseData>>({
                    executor: 'page',
                    commandType: 'closeResource',
                    payload: message as IIFrameMessage<IIFrameResourceActionBaseData>
                });
                break;
            case TridentIFrameMessageType.SetShowAuthoringTab:
                this._requestSetShowAuthoringTab$.next(message.data as IIFrameSetShowAuthoringTabData);
                break;
            case TridentIFrameMessageType.ListResources:
                this._listResources(message as IIFrameMessage<IIFrameListResourcesMessageData>);
                break;
            case TridentIFrameMessageType.ListMetadata:
                this._listMetadata(message);
                break;
            case TridentIFrameMessageType.GetStyleFileUrl:
                this._getStyleFileUrl(message);
                break;
            case TridentIFrameMessageType.GetIframeCustomProfile:
                try {
                    const profile = JSON.parse(
                        JSON.stringify(
                            iFrameCustomerProfiles[(message as IIFrameMessage<keyof typeof IFrameProfile>).data]
                        )
                    );
                    this.requestMessage({
                        ...message,
                        data: profile
                    });
                } catch (ex) {
                    this.requestMessage({
                        ...message,
                        data: undefined,
                        error: JSON.stringify(ex.message || ex)
                    });
                }
                break;
            case TridentIFrameMessageType.OpenActivityRuns:
                queueCommand<IIFrameMessage<IIFrameOpenMonitoringPageRequestData> & IIFrameResourceContextBase>({
                    executor: 'page',
                    commandType: 'openActivityRuns',
                    payload: {
                        ...(message as IIFrameMessage<IIFrameOpenMonitoringPageRequestData>),
                        contextData: (message.data as IIFrameOpenMonitoringPageRequestData).contextData
                    }
                });
                break;
            case TridentIFrameMessageType.OpenCopyJobRun:
                queueCommand<IIFrameMessage<IIFrameOpenCopyJobRunPageRequestData> & IIFrameResourceContextBase>({
                    executor: 'page',
                    commandType: 'openCopyJobRun',
                    payload: {
                        ...(message as IIFrameMessage<IIFrameOpenCopyJobRunPageRequestData>),
                        contextData: (message.data as IIFrameOpenCopyJobRunPageRequestData).contextData
                    }
                });
                break;
            case TridentIFrameMessageType.PanelClosed:
                queueCommand<IIFrameResourceContextBase>({
                    executor: 'panel',
                    commandType: 'clearPanel',
                    payload: message.data as IIFrameResourceContextBase
                });
                break;
            case TridentIFrameMessageType.DialogClosed:
                queueCommand<IIFrameResourceContextBase>({
                    executor: 'dialog',
                    commandType: 'clearDialog',
                    payload: message.data as IIFrameResourceContextBase
                });
                break;
            case TridentIFrameMessageType.AutoAlign:
                this._requestAutoAlign$.next(message.data as IIFrameResourceActionBaseData);
                break;
            case TridentIFrameMessageType.ZoomToFit:
                this._requestZoomToFit$.next(message.data as IIFrameResourceActionBaseData);
                break;
            case TridentIFrameMessageType.Zoom:
                if (message.data) {
                    this.zoomRequest(message.data as IIFrameZoomOptionData);
                }
                break;
            case TridentIFrameMessageType.DiscardAll:
                this._requestDiscardAll$.next(message as IIFrameMessage<IIFrameDiscardChangeData>);
                break;
            case TridentIFrameMessageType.UpdateTheme: {
                const themeType = (message.data as IIFrameUpdateThemeData | undefined)?.themeType;
                if (themeType) {
                    this._requestThemeUpdate$.next(themeType);
                }
                break;
            }
            case TridentIFrameMessageType.UpdateStoredArtifacts:
                this._requestUpdateStoredArtifacts$.next(message as IIFrameMessage<IIFrameUpdateStoredArtifactsData>);
                break;
            case TridentIFrameMessageType.ConfirmDialogClose:
                this._requestConfirmDialogClose$.next();
                break;
            case TridentIFrameMessageType.EnsureResourceContextModule: {
                const { contextData } = (message.data || {}) as IIFrameResourceContextBase;
                await this._injector.get(BaseAppModuleManagementService).ensureResourceContextModule(contextData);
                this.requestMessage({
                    ...message
                });
                break;
            }
            case TridentIFrameMessageType.ExportTemplate:
                this._requestExportTemplate$.next(message.data as IIFrameResourceActionBaseData);
                break;
            case TridentIFrameMessageType.ImportTemplate:
                this._requestImportTemplate$.next(message.data as IIFrameImportPipelineTemplateData);
                break;
            case TridentIFrameMessageType.OpenTemplateGallery:
                this._requestOpenTemplateGallery$.next(message.data as IIFrameOpenTemplateGalleryData);
                break;
            case TridentIFrameMessageType.ViewNotificationActionDetails:
                const actionData = message.data as shellapi.notifications.ITridentNotificationActionData;
                switch (actionData?.detailedActionName) {
                    case 'viewPipelineRunError':
                        const payload = {
                            contextData: actionData.actionDetails?.contextData,
                            width: '600px',
                            data: actionData.actionDetails
                        } as unknown as IOpenSidenavCommandPayload & { data: { message: string } };
                        queueCommand<IOpenSidenavCommandPayload & { data: { message: string } }>({
                            preparer: 'worker',
                            executor: 'panel',
                            commandType: 'showPipelineRunErrorDetail',
                            payload
                        });
                        break;
                    case 'viewPipelineRunDetails':
                        const pipelineRunId = actionData.actionDetails?.pipelineRunId as string;
                        const pipelineName = actionData.actionDetails?.pipelineName as string;
                        const contextData = actionData.actionDetails.contextData;
                        if (!stringIsNullOrEmpty(pipelineRunId) && !stringIsNullOrEmpty(pipelineName)) {
                            queueCommand<IIFrameOpenPageRequestData>({
                                executor: 'worker',
                                commandType: 'openPage',
                                payload: {
                                    path: `/monitoring/workspaces/${contextData.workspaceObjectId}/mounteddatafactory/${contextData.contextId}/pipelines/${pipelineName}/${pipelineRunId}`
                                }
                            });
                        }
                        break;
                    default:
                        this._loggerService.warn(
                            'IFrameMessengerService',
                            `Invalid notification action name: ${actionData.detailedActionName}`
                        );
                }
                break;
            case TridentIFrameMessageType.OpenMountedDataFactory:
                queueCommand<IIFrameResourceContextBase & IIFrameMessage<IIFrameOpenMountedDataFactory>>({
                    executor: 'page',
                    commandType: 'openMountedDataFactory',
                    payload: {
                        contextData: (message as IIFrameMessage<IIFrameOpenMountedDataFactory>).data.contextData,
                        ...(message as IIFrameMessage<IIFrameOpenMountedDataFactory>)
                    }
                });
                break;
            case TridentIFrameMessageType.RequestSaveAll:
                this._requestSaveAll$.next(message as IIFrameMessage<IIFrameResourceActionBaseData>);
                break;
            // #endregion
            default:
                this._message$.next(message);
        }
    }

    private async _getServiceInstance<R>(contextData: IResourceContext, serviceName: string): Promise<R> {
        return multiContextRootModuleAsEntry
            ? await this._injector.get(BaseAppModuleManagementService).getScopedService<R>(contextData, serviceName)
            : this._injector.get(serviceName);
    }

    private _getLoggablePayload(message: IIFrameMessage): unknown {
        switch (message.type) {
            case TridentIFrameMessageType.EnsureResourceContextModule:
                return pick(message.data as IIFrameResourceContextBase, 'contextData');
            case TridentIFrameMessageType.OpenCodeEditor:
                return message.data as IIFrameOpenResourceInTridentWsData;
            case TridentIFrameMessageType.AddActivity:
                return message.data as IIFrameAddActivityData;
            case TridentIFrameMessageType.ListResources:
                return message.data as IIFrameListResourcesMessageData;
            default:
                return undefined;
        }
    }
}
